import React from 'react';

import {ThemeProvider as MuiThemeProvider} from '@material-ui/core/styles';
import {createMuiTheme} from '@material-ui/core';

const rootAppTheme = createMuiTheme({
    palette: {
        grey: {
            900: '#2f353d',
            800: '#677282',
            700: '#7b8a9d',
            600: '#cad0d8',
            500: '#e8edf1',
            400: 'rgba(123, 138, 157, 0.14)',
            300: 'rgba(232, 237, 241, 0.6)',
            200: 'rgba(247, 248, 250, 0.55)',
        },
        primary: {
            main: '#0096ff',
        }
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
        }
    },
    spacing: factor => ([4, 8, 10, 14, 16, 20, 24, 28, 34, 56, 100][factor]),
});
const theme = (rootTheme) => ({
    ...rootTheme,
    typography: {
        ...rootTheme.typography,
        h1: {
            fontWeight: 300,
            lineHeight: '40px',
            fontSize: 28,
            color: rootTheme.palette.grey[800],
            [rootTheme.breakpoints.down('sm')]: {
                fontSize: 28,
            },
        },
        h2: {
            fontSize: 26,
            fontWeight: 300,
            lineHeight: '36px',
            color: rootTheme.palette.grey[900],
            [rootTheme.breakpoints.down('sm')]: {
                fontSize: 26,
            },
        },
        h3: {
            fontSize: 22,
            fontWeight: 300,
            lineHeight: '32px',
            color: rootTheme.palette.grey[900],
        },
        h4: {
            fontSize: 14,
            fontWeight: 'normal',
            lineHeight: 1.5,
            textAlign: 'left',
            color: rootTheme.palette.grey[700],
            [rootTheme.breakpoints.down('sm')]: {
                fontSize: 14,
            },
        },
        h5: {
            fontSize: 16,
            fontWeight: '600',
            lineHeight: 1.5,
            color: rootTheme.palette.grey[900],
            [rootTheme.breakpoints.down('sm')]: {
                fontSize: 16,
            },
        },
        h6: {
            fontSize: 18,
            fontWeight: '300',
            lineHeight: 1.5,
            color: rootTheme.palette.grey[900],
        },
        body1: {
            fontSize: 14,
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontStretch: 'normal',
            letterSpacing: 'normal',
            lineHeight: '21px',
            color: rootTheme.palette.grey[900],
        },
        body2: {
            fontSize: 14,
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontStretch: 'normal',
            letterSpacing: 'normal',
            lineHeight: '21px',
            color: rootTheme.palette.grey[600],
        },
        subtitle1: {
            fontSize: 12,
            lineHeight: '18px',
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontStretch: 'normal',
            letterSpacing: 'normal',
            color: rootTheme.palette.grey[700],
        },
        subtitle2: {
            fontSize: 14,
            lineHeight: '21px',
            fontWeight: 600,
            fontStyle: 'normal',
            fontStretch: 'normal',
            letterSpacing: 'normal',
            color: rootTheme.palette.grey[700],
        },
    },
    overrides: {
        MuiFilledInput: {
            root: {
                backgroundColor: '#f7f8fa',
                border: 'none',
                minHeight: 40,

                '&:hover': {
                    backgroundColor: '#f7f8fa',
                },
            },
            input: {
                padding: '10px 12px',
                '&::placeholder': {
                    color: '#afb8c4',
                }
            },
            focused: {
                backgroundColor: `#f7f8fa !important`,
                border: 'none !important',
            },
            error: {
                backgroundColor: `rgba(255, 95, 119, .1) !important`,
                color: `rgb(255, 95, 119) !important`,
                caretColor: `rgb(255, 95, 119) !important`,
            }
        },
        MuiInputLabel: {
            outlined: {
                transform: 'translate(14px, 17px) scale(1)',
            },
            shrink: {
                transform: 'translate(0, -20px) scale(0.9)',
            }
        },
        MuiFormHelperText: {
            root: {
                margin: '0 !important',
                backgroundColor: 'transparent !important',
                fontSize: 13,
                fontFamily: 'Open Sans,Arial,sans-serif',
                letterSpacing: 0,
            },
        },
        MuiTextField: {
            root: {
                width: '100%',
            },
            focused: {
                backgroundColor: '#fff',
            }
        },
        MuiDivider: {
            root: {
                backgroundColor: rootTheme.palette.grey[400],
                height: 2,
                margin: 0,
                width: '100%',
            },
            vertical: {
                width: 2,
                height: '100%'
            }
        },
        MuiDialog: {
            paper: {
                backgroundColor: '#f7f8fa',
            }
        },
        MuiCard: {
            root: {
                boxShadow: '0 4px 15px 0 rgba(0, 0, 0, 0.05)',
                borderRadius: 4,
                width: '100%',
            }
        },
        MuiSnackbarContent: {
            root: {
                backgroundColor: rootTheme.palette.primary.main,
                minWidth: 'initial !important',
            }
        },
        MuiAutocomplete: {
            tag: {
                height: rootTheme.spacing(4),
                fontSize: 13,
            },
            popper: {
                zIndex: '10000 !important',
            },
            inputRoot: {
                paddingRight: '56px !important',
                paddingLeft: '0 !important',
                paddingTop: '0 !important',
                '&[class*="MuiOutlinedInput-root"]': {
                    paddingTop: '9px !important',
                }
            },
            clearIndicator: {
                visibility: 'visible !important',
            },
            root: {
                '&$focused': {
                    backgroundColor: 'transparent !important',
                },
            }
        },
        MuiChip: {
            deleteIcon: {
                width: rootTheme.spacing(2),
            }
        },
        MuiPaginationItem: {
            root: {
                height: 30,
                width: 30,
                // TODO: Replace to custom palette property
                color: '#2f353d',
            },
            selected: {
                backgroundColor: `${rootTheme.palette.primary.main} !important`,
                color: '#fff',
            }
        },
        MuiTouchRipple: {
            root: {
                zIndex: 10,
            },
        },
        MuiRadio: {
            root: {
                color: `rgba(123, 138, 157, 0.24)`,
                height: 22,
                width: 22,
                padding: 3,
            },
            checked: {
                color: rootTheme.palette.primary.main,
            },
        },
        MuiSvgIcon: {
            root: {
                // width: 'initial',
                // height: 'initial',
            }
        },
        MuiOutlinedInput: {
            adornedEnd: {
                paddingRight: '0 !important',
            },
            input: {
                height: '100% !important',
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
            },
        },
        MuiSelect: {
            icon: {
                margin: '0 5px !important',
            },
            select: {
                marginTop: '0 !important'
            }
        },
        MuiPopover: {
            root: {
                zIndex: '10001 !important',
            }
        },
        MuiFormControl: {
            root: {
                width: '100%',
            }
        },
    }
});

export default function ThemeProvider({children}) {
    return (
        <MuiThemeProvider
            theme={rootAppTheme}
        >
            <MuiThemeProvider
                theme={theme}
            >
                {children}
            </MuiThemeProvider>
        </MuiThemeProvider>
    );
}



// WEBPACK FOOTER //
// ./src/components/ThemeProvider.jsx